import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Skeleton } from '../components/Skeleton';
import CopyButton from '../components/CopyButton';
import Icon from '../components/Icon';
import PulseDot from '../components/PulseDot';
import { formatDateTime } from '../lib/time';

function Section({ title, desc, children }) {
  return (
    <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
      <h3 style={{ fontSize: '1rem', marginBottom: desc ? 6 : 14 }}>{title}</h3>
      {desc && <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 14 }}>{desc}</p>}
      {children}
    </div>
  );
}

export default function LicensePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const { reloadLicense, activeServerKey } = useAuth();

  const [loading, setLoading] = useState(true);
  const [license, setLicense] = useState(null);
  const [billing, setBilling] = useState(null);
  const [cards, setCards] = useState([]);
  const [migrateLabel, setMigrateLabel] = useState('');
  const [msg, setMsg] = useState(null); // { tone, text }
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const [lic, bill, cardRes] = await Promise.all([api.license(activeServerKey || undefined), api.billing(), api.listCards()]);
    setLicense(lic.license);
    setBilling(bill);
    setCards(cardRes.cards || []);
  }, [activeServerKey]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const flash = (tone, text) => {
    setMsg({ tone, text });
    setTimeout(() => setMsg(null), 6000);
  };

  const doMigrate = async () => {
    if (!window.confirm(t('license.migrate_confirm'))) return;
    setBusy('migrate');
    try {
      await api.migrateLicense(migrateLabel.trim() || undefined);
      setMigrateLabel('');
      flash('ok', t('license.migrate_done'));
      await load();
      await reloadLicense();
    } catch (e) {
      flash('crit', e.message);
    } finally {
      setBusy('');
    }
  };

  const toggleAutoRenew = async () => {
    setBusy('autorenew');
    try {
      const res = await api.setAutoRenew(!billing.subscription.autoRenew);
      setBilling((b) => ({ ...b, subscription: { ...b.subscription, autoRenew: res.subscription.autoRenew } }));
    } catch (e) {
      flash('crit', e.message);
    } finally {
      setBusy('');
    }
  };

  const doPause = async () => {
    if (!window.confirm(t('license.pause_confirm'))) return;
    setBusy('pause');
    try {
      await api.pauseSubscription();
      flash('ok', t('license.pause_done'));
      await load();
      await reloadLicense();
    } catch (e) {
      flash('crit', e.message);
    } finally { setBusy(''); }
  };

  const doResume = async () => {
    setBusy('resume');
    try {
      await api.resumeSubscription();
      flash('ok', t('license.resume_done'));
      await load();
      await reloadLicense();
    } catch (e) {
      flash('crit', e.message);
    } finally { setBusy(''); }
  };

  const doRenew = async () => {
    setBusy('renew');
    try {
      const res = await api.renewNow();
      if (res.ok && res.status === 'success') flash('ok', t('license.renew_ok'));
      else if (res.reason === 'no_card' || res.reason === 'no_utoken') flash('crit', t('license.renew_no_card'));
      else flash('crit', `${t('license.renew_fail')}${res.error ? ': ' + res.error : ''}`);
      await load();
      await reloadLicense();
    } catch (e) {
      flash('crit', e.message);
    } finally {
      setBusy('');
    }
  };

  const deleteCard = async (ctoken) => {
    if (!window.confirm(t('license.card_delete_confirm'))) return;
    try {
      await api.deleteCard(ctoken);
      await load();
    } catch (e) {
      flash('crit', e.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t('license.title')} crumb={t('dashboard.title')} heading={false}>
        <Skeleton style={{ height: 24, width: 200 }} />
        <Skeleton className="sk-block" style={{ marginTop: 20 }} />
      </DashboardLayout>
    );
  }

  const sub = billing?.subscription;
  const statusKey = { active: 'status_active', suspended: 'status_suspended', revoked: 'status_revoked' }[license?.status] || 'status_active';
  const subStatusKey = { active: 'sub_active', past_due: 'sub_past_due', expired: 'sub_expired', pending: 'sub_pending', paused: 'sub_paused' }[sub?.status] || 'sub_pending';
  const subTone = sub?.status === 'active' ? 'ok' : (sub?.status === 'past_due' || sub?.status === 'paused') ? 'warn' : 'crit';

  return (
    <DashboardLayout title={t('license.title')} crumb={t('dashboard.title')} heading={false}>
      <h1 className="page-title">{t('license.title')}</h1>
      <p className="page-sub">{t('license.subtitle')}</p>

      {msg && (
        <div className={`form-error`} style={{
          background: msg.tone === 'ok' ? 'var(--ok-soft)' : 'var(--crit-soft)',
          borderColor: 'transparent', color: msg.tone === 'ok' ? 'var(--ok)' : 'var(--crit)',
        }}>
          <Icon name={msg.tone === 'ok' ? 'check' : 'alert-triangle'} size={14} /> {msg.text}
        </div>
      )}

      {!license ? (
        <div className="empty-state">
          <Icon name="key" size={28} style={{ color: 'var(--text-3)' }} />
          <h3 style={{ marginTop: 12 }}>{t('onboarding.no_license')}</h3>
        </div>
      ) : (
        <>
          {/* Lisans */}
          <Section title={t('license.key')}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '10px 14px', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 15, letterSpacing: '0.04em', color: 'var(--accent)', fontFamily: 'ui-monospace, monospace' }}>{license.key}</code>
              <CopyButton text={license.key} />
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 14, flexWrap: 'wrap', fontSize: 13 }}>
              <span style={{ color: 'var(--text-3)' }}>{t('license.status')}: <span className={`badge ${license.status === 'active' ? 'ok' : 'crit'}`}>{t(`license.${statusKey}`)}</span></span>
              <span style={{ color: 'var(--text-3)' }}>{t('license.server_label')}: <strong style={{ color: 'var(--text)' }}>{license.serverLabel || t('license.no_label')}</strong></span>
            </div>
            {license.needsReinstall && (
              <div className="badge warn" style={{ marginTop: 12 }}>
                <Icon name="alert-triangle" size={12} /> {t('license.needs_reinstall')}
              </div>
            )}
          </Section>

          {/* Abonelik */}
          {sub && (
            <Section title={t('checkout.title')}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 16 }}>
                <Field label={t('license.plan')} value={t(`checkout.plan_${sub.plan}`)} />
                <Field label={t('license.status')} value={<span className={`badge ${subTone}`}>{t(`license.${subStatusKey}`)}</span>} />
                <Field
                  label={sub.autoRenew && sub.status === 'active' ? t('license.renews_on') : t('license.expires_on')}
                  value={formatDateTime(sub.currentPeriodEnd, lang)}
                />
                {sub.serverSlots != null && (
                  <Field label={t('license.servers')} value={
                    <Link to="/dashboard/servers" style={{ color: 'var(--accent)' }}>
                      {t('admin.slots_n', { n: sub.serverSlots })}
                    </Link>
                  } />
                )}
              </div>
              {sub.status === 'paused' ? (
                <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 10 }}>
                    {t('license.paused_info', {
                      days: sub.pausedRemainingDays,
                      until: sub.pauseAutoResumeAt ? formatDateTime(sub.pauseAutoResumeAt, lang).slice(0, 10) : '',
                    })}
                  </p>
                  <button className="btn btn-sm" onClick={doResume} disabled={busy === 'resume'}>
                    <Icon name="activity" size={13} /> {busy === 'resume' ? t('common.loading') : t('license.resume')}
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                    <button
                      className={`badge ${sub.autoRenew ? 'ok' : ''}`}
                      style={{ cursor: 'pointer', border: sub.autoRenew ? 'none' : '1px solid var(--border)' }}
                      onClick={toggleAutoRenew} disabled={busy === 'autorenew'}
                    >
                      <PulseDot tone={sub.autoRenew ? 'ok' : 'idle'} animated={false} />
                      {t('license.auto_renew')}: {sub.autoRenew ? t('license.auto_renew_on') : t('license.auto_renew_off')}
                    </button>
                    <button className="btn btn-subtle btn-sm" onClick={doRenew} disabled={busy === 'renew'}>
                      <Icon name="activity" size={12} /> {busy === 'renew' ? t('license.renewing') : t('license.renew_now')}
                    </button>
                  </div>
                  {sub.status === 'active' && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t('license.pause_title')}</div>
                      <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 10 }}>
                        {t('license.pause_desc', { max: sub.pauseMaxDays })}
                        {sub.pausesLeft != null && ` (${t('license.pauses_left', { n: sub.pausesLeft })})`}
                      </p>
                      <button className="btn btn-ghost btn-sm" onClick={doPause}
                        disabled={busy === 'pause' || !sub.canPause}
                        title={!sub.canPause ? t('license.pause_unavailable', { min: sub.pauseMinDays }) : ''}>
                        {busy === 'pause' ? t('common.loading') : t('license.pause')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </Section>
          )}

          {/* Sunucu taşıma */}
          <Section title={t('license.migrate_title')} desc={t('license.migrate_desc')}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                value={migrateLabel}
                onChange={(e) => setMigrateLabel(e.target.value)}
                placeholder={t('license.migrate_label_ph')}
                style={{ flex: 1, minWidth: 220 }}
              />
              <button className="btn btn-subtle" onClick={doMigrate} disabled={busy === 'migrate'}>
                <Icon name="arrow" size={13} /> {t('license.migrate_btn')}
              </button>
            </div>
          </Section>

          {/* Kartlar */}
          <Section title={t('license.cards_title')}>
            {cards.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{t('license.cards_empty')}</p>
            ) : (
              <div className="list">
                {cards.map((c) => (
                  <div className="list-row" key={c.id || c.ctoken} style={{ padding: '10px 0' }}>
                    <Icon name="key" size={15} style={{ color: 'var(--text-3)' }} />
                    <div className="row-main">
                      <strong>{c.brand || 'Kart'}</strong> •••• {c.last4 || '····'}
                      {c.expMonth && <span style={{ color: 'var(--text-3)', marginLeft: 8, fontSize: 12 }}>{c.expMonth}/{c.expYear}</span>}
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => deleteCard(c.ctoken)}>
                      <Icon name="trash" size={12} /> {t('license.card_delete')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Ödeme geçmişi */}
          <Section title={t('license.history_title')}>
            {!billing?.transactions?.length ? (
              <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{t('license.history_empty')}</p>
            ) : (
              <div className="list">
                {billing.transactions.map((tx) => (
                  <div className="list-row" key={tx.id} style={{ padding: '10px 0' }}>
                    <span className={`badge ${tx.status === 'success' ? 'ok' : tx.status === 'failed' ? 'crit' : ''}`}>
                      {t(`license.tx_${tx.status}`)}
                    </span>
                    <div className="row-main">
                      ₺{Number(tx.amount).toFixed(2)}
                      {tx.recurring && <span style={{ color: 'var(--text-3)', fontSize: 11, marginLeft: 8 }}>({t('license.tx_recurring')})</span>}
                      {tx.comp && <span style={{ color: 'var(--text-3)', fontSize: 11, marginLeft: 8 }}>({t('license.tx_comp')})</span>}
                    </div>
                    <div className="row-time">{formatDateTime(tx.createdAt, lang)}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </>
      )}
    </DashboardLayout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value}</div>
    </div>
  );
}
